import { useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "../MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { getVoters } from "../utilities";
import type {
  AgendaItemMotion,
  AgendaItemReview,
  MotionType,
} from "@repo/types";
import { MotionPanel } from "@/components/common/MotionPanel";
import { MotionListRenderer } from "../../MotionRenderer";
import { cn } from "@repo/shared/utils";
import { CollapsiblePanel } from "../CollapsiblePanel";
import { getSectionById } from "./sections";
import type { MeetingRunSectionProps } from "./meetingRunSectionProps";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunMotionsSection = ({
  id,
  idSuffix,
  className,
  open,
  onToggle,
}: MeetingRunSectionProps) => {
  const {
    tenantId,
    meeting: _meeting,
    agendaItem: _item,
    mutationSystem,
    team,
    userMap,
    motionTypeMap,
    noTimestamps,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    "meeting:" + _meeting.id,
    _meeting
  );

  const votingUsers = getVoters({ meeting, team, userMap });

  const insertMotion = (motion: MotionType) => {
    if (noTimestamps) return;

    // prepare new motion
    const newMotion: AgendaItemMotion = {
      motionId: motion.id,
      timestamp: Date.now(),
      resolved: !motion.voting,
    };

    // prepare review motions array
    const review: AgendaItemReview = agendaItem.review ?? {
      meetingId: meeting.id,
    };
    review.motions ??= [];

    // find deepest unresolved nesting
    let list: AgendaItemMotion[] = review.motions;
    let lastMotion = list[list.length - 1];
    while (
      lastMotion &&
      !lastMotion.resolved &&
      motionTypeMap.item[lastMotion.motionId]?.nestable
    ) {
      list = lastMotion.nesting ??= [];
      lastMotion = list[list.length - 1];
    }

    // add this motion to the unresolved nesting
    list.push(newMotion);

    // update agendaItem
    mutationSystem.debounce(
      mutationKey,
      agendaItemMutation,
      { review },
      agendaItem.id
    );
  };

  return (
    <CollapsiblePanel
      id={id}
      idSuffix={idSuffix}
      title={getSectionById("motions").title}
      className={className}
      open={open}
      onToggle={onToggle}
    >
      <div className="flex flex-row gap-x-2 mb-4">
        {!noTimestamps && (
          <MotionPanel
            className="m-1 shadow-[0_0_6px_2px_white]"
            motionFor="item"
            corner="topleft"
            label={
              <>
                Secondary
                <br />
                Motion
              </>
            }
            onPickMotion={insertMotion}
          />
        )}
        <h1 className={cn("mt-5 text-2xl font-bold", noTimestamps || "ml-1")}>
          Motions
        </h1>
      </div>

      <MotionListRenderer
        motionTypes={motionTypeMap.item}
        motions={agendaItem.review?.motions ?? []}
        votingUsers={votingUsers.all}
        absentVoters={votingUsers.absent}
        team={team}
        tenantId={tenantId}
        onChange={() => {
          const review = { ...agendaItem.review, meetingId: meeting.id };
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { review },
            agendaItem.id
          );
        }}
        onMotionChange={(amended) => {
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { motion: amended },
            agendaItem.id
          );
        }}
        onMotionDeferred={() => {
          const review: AgendaItemReview = {
            ...agendaItem.review,
            meetingId: meeting.id,
            status: "deferred",
          };

          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { review },
            agendaItem.id
          );
        }}
        context={{
          motionText: agendaItem.motion ?? "",
          meeting: meeting,
          agendaItem: agendaItem,
          mutationSystem,
        }}
      />
    </CollapsiblePanel>
  );
};
